import { forwardRef, type ReactNode } from 'react'
import { motion, useReducedMotion, type HTMLMotionProps } from 'framer-motion'
import { cx, ui, focus, interaction } from '../../lib/ui-primitives'
import { springs, tapVariants } from '../../lib/motion'

// ============================================
// MOTION BUTTON
// Button with physics-based tap/hover animations
// ============================================

export type MotionButtonVariant = 'primary' | 'secondary' | 'ghost' | 'quiet' | 'danger'
export type MotionButtonSize = 'sm' | 'md' | 'lg'

const variantStyles: Record<MotionButtonVariant, string> = {
  primary: 'text-white font-semibold',
  secondary: 'bg-indigo-600 hover:bg-indigo-500 text-white',
  ghost: ui.btnGhost,
  quiet: ui.btnQuiet,
  danger: ui.btnDanger,
}

const sizeStyles: Record<MotionButtonSize, string> = {
  sm: 'px-3 py-2 text-sm min-h-[40px]',
  md: 'px-4 py-3 text-base min-h-[48px]',
  lg: 'px-6 py-4 text-lg min-h-[56px] font-bold',
}

export interface MotionButtonProps extends Omit<HTMLMotionProps<'button'>, 'ref' | 'children'> {
  variant?: MotionButtonVariant
  size?: MotionButtonSize
  fullWidth?: boolean
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  bgColor?: string
  children?: ReactNode
}

/**
 * MotionButton - Button with Framer Motion physics
 *
 * Features:
 * - Spring-based tap/hover animations
 * - Respects reduced motion preference
 * - All features from regular Button
 */
export const MotionButton = forwardRef<HTMLButtonElement, MotionButtonProps>(
  (
    {
      variant = 'ghost',
      size = 'md',
      fullWidth = false,
      loading = false,
      leftIcon,
      rightIcon,
      bgColor,
      className,
      disabled,
      children,
      style,
      ...props
    },
    ref
  ) => {
    const shouldReduceMotion = useReducedMotion()
    const isDisabled = disabled || loading

    // Motion config
    const motionProps = shouldReduceMotion
      ? {}
      : {
          whileTap: isDisabled ? undefined : tapVariants.tap,
          whileHover: isDisabled ? undefined : tapVariants.hover,
          transition: springs.snappy,
        }

    return (
      <motion.button
        ref={ref}
        disabled={isDisabled}
        className={cx(
          'inline-flex items-center justify-center gap-2 rounded-xl font-medium',
          'transition-colors', // Only transition colors, motion handles scale
          focus.ring,
          interaction.disabled,
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        style={{
          ...(bgColor && variant === 'primary' ? { backgroundColor: bgColor } : {}),
          ...style,
        }}
        {...motionProps}
        {...props}
      >
        {loading ? (
          <motion.span
            className="size-4 border-2 border-current/30 border-t-current rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            aria-hidden="true"
          />
        ) : leftIcon ? (
          <span className="shrink-0" aria-hidden="true">
            {leftIcon}
          </span>
        ) : null}

        {children}

        {rightIcon && !loading && (
          <span className="shrink-0" aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </motion.button>
    )
  }
)

MotionButton.displayName = 'MotionButton'
